// client/src/pages/admin/adminWriteKnowledge.jsx

import React, { useContext, useState } from 'react';
import { GlobalContext } from '../../context/provider';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import useBlobStorage from '../../hooks/useBlobStorage'

import './adminWriteKnowledge.css';
import '../../styles/globalstyles.css';

const AdminWriteKnowledge = () => {
  const [postSubject, setPostSubject] = useState('');
  const [postContent, setPostContent] = useState('');
  const [isSending, setIsSending] = useState(false);

  const { authState } = useContext(GlobalContext);
  const { authData } = authState;
  const { createKnowledgePost, listKnowledgePosts } = useBlobStorage();

  const handleSendPost = async () => {
    if (!postSubject.trim()) {
      showNotification('⚠️ Please enter a subject for your knowledge post.', 'warning');
      return;
    }

    if (!postContent.trim()) {
      showNotification('⚠️ Please write content before sending.', 'warning');
      return;
    }

    try {
      setIsSending(true);
      
      // Get next filename
      const filename = await getNextPostFilename();
      
      // Create knowledge post
      const result = await createKnowledgePost({
        message: `Subject: ${postSubject}\n\n${postContent}`,
        loginId: authData.id,
        filename,
      });

      if (result.success) {
        showNotification('✅ Knowledge post published successfully!', 'success');
        setPostContent('');
        setPostSubject('');
      } else {
        showNotification('❌ Failed to publish post. Please try again.', 'error');
      }
    } catch (error) {
      console.error('❌ Error publishing knowledge post:', error.message || error);
      showNotification('❌ Failed to publish post. Please try again.', 'error');
    } finally {
      setIsSending(false);
    }
  };

  const showNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.innerHTML = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'success' ? '#10b981' : type === 'error' ? '#ef4444' : '#f59e0b'};
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      z-index: 10000;
      box-shadow: 0 10px 30px rgba(0,0,0,0.3);
      animation: slideDown 0.3s ease-out;
      font-weight: 500;
    `;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideUp 0.3s ease-out';
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const getNextPostFilename = async () => {
    try {
      const result = await listKnowledgePosts();
      const count = result.number || 0;
      const folderName = 'admin-knowledge';
      return `${folderName}/${count + 1}.txt`;
    } catch (err) {
      console.error('❌ Error generating filename:', err);
      return `admin-knowledge/1.txt`;
    }
  };

  const charCount = postContent.length;
  const maxChars = 2000;
  const charPercentage = (charCount / maxChars) * 100;

  const subjectCharCount = postSubject.length;
  const maxSubjectChars = 100;

  const profileHeaderData = { 
    ...authData, 
    title: '✍️ Write Knowledge Post',
    subTitle: 'Share IoT insights, platform updates, or learning resources<'
  };

  return (
    <div className="main-container">
      <div className="main-content">
        <div className="header-row">
          <ProfileHeader data={profileHeaderData} activePage="write-knowledge" />
          <div className="headerMiddle">
            <img src="/images/logo.png" alt="Platform Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={0} icons={[]} />
        </div>

        {/* <div className="page-title">
          <h3>✍️ Write Knowledge Post</h3>
          <p>Share IoT insights, platform updates, or learning resources</p>
        </div> */}

        <div className="scrollable-list">
          {/* Author Info Card */}
          <div className="author-info-card">
            <img 
              src={authData?.imageUrl || '/images/default-avatar.png'} 
              alt="Author" 
              className="author-avatar"
            />
            <div className="author-details">
              <h4 className="author-name">{authData?.name || 'Administrator'}</h4>
              <p className="author-role">Platform Administrator</p>
            </div>
            <div className="author-badge">
              <span className="badge-icon">📝</span>
              <span className="badge-text">Writing</span>
            </div>
          </div>

          {/* Tips Section */}
          <div className="tips-section">
            <div className="tip-icon">💡</div>
            <div className="tips-content">
              <p className="tips-title">Tips for effective knowledge posts:</p>
              <ul className="tips-list">
                <li>Use clear, descriptive subjects</li>
                <li>Provide actionable IoT insights</li>
                <li>Include examples or use cases when relevant</li>
                <li>Keep technical content accessible</li>
              </ul>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="quick-stats-grid">
            <div className="note-stat-box">
              <div className="note-stat-icon">📝</div>
              <div className="note-stat-info">
                <p className="note-stat-label">Words</p>
                <p className="note-stat-value">{postContent.split(/\s+/).filter(w => w).length}</p>
              </div>
            </div>
            <div className="note-stat-box">
              <div className="note-stat-icon">⏱️</div>
              <div className="note-stat-info">
                <p className="note-stat-label">Est. Read Time</p>
                <p className="note-stat-value">{Math.max(1, Math.ceil(postContent.split(/\s+/).filter(w => w).length / 200))} min</p>
              </div>
            </div>
            <div className="note-stat-box">
              <div className="note-stat-icon">✅</div>
              <div className="note-stat-info">
                <p className="note-stat-label">Status</p>
                <p className="note-stat-value">{postSubject && postContent ? 'Ready' : 'Draft'}</p>
              </div>
            </div>
          </div>

          {/* Writing Card */}
          <div className="full-card">
            <div className="card-header-section">
              <div className="section-icon">📋</div>
              <h4>Compose Knowledge Post</h4>
            </div>

            {/* Subject Input */}
            <div className="form-group-write">
              <label className="input-label">
                <span className="label-icon">📌</span>
                Subject
              </label>
              <input
                type="text"
                className="subject-input-modern"
                placeholder="e.g., 'Lab 5 Update: New MQTT Features' or 'IoT Best Practice: Sensor Calibration'"
                value={postSubject}
                onChange={(e) => setPostSubject(e.target.value)}
                maxLength={maxSubjectChars}
              />
              <div className="char-counter">
                <span className={subjectCharCount > 80 ? 'warning' : ''}>
                  {subjectCharCount}/{maxSubjectChars}
                </span>
              </div>
            </div>

            {/* Content Textarea */}
            <div className="form-group-write">
              <label className="input-label">
                <span className="label-icon">💭</span>
                Content
              </label>
              <textarea
                className="advice-textarea-modern"
                placeholder="Share detailed IoT knowledge, platform updates, lab tips, troubleshooting guides, or learning resources...

Example:
We've added new MQTT debugging features to Lab 5. Students can now:
- View real-time message logs
- Test connection strings before deployment
- Monitor message frequency and payload size

This will help identify common connectivity issues early in development."
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                maxLength={maxChars}
              />
              
              {/* Character Progress Bar */}
              <div className="char-progress-container">
                <div className="char-progress-bar">
                  <div 
                    className="char-progress-fill"
                    style={{ 
                      width: `${charPercentage}%`,
                      background: charPercentage > 90 ? '#ef4444' : charPercentage > 70 ? '#f59e0b' : '#10b981'
                    }}
                  ></div>
                </div>
                <span className={`char-count-text ${charPercentage > 90 ? 'danger' : ''}`}>
                  {charCount} / {maxChars} characters
                </span>
              </div>
            </div>

            {/* Send Button */}
            <button
              className="send-button-modern"
              onClick={handleSendPost}
              disabled={isSending || !postSubject.trim() || !postContent.trim()}
            >
              {isSending ? (
                <>
                  <span className="button-spinner"></span>
                  Publishing...
                </>
              ) : (
                <>
                  <span className="button-icon">📤</span>
                  Publish Knowledge Post
                  <span className="button-arrow">→</span>
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminWriteKnowledge;